<template>
  <div class="hero-carousel-item" :data-test-id="`slide-${index}`">
    <div
      class="hero-carousel-item__img-wrapper"
      :class="{
        'hero-carousel-item__img-wrapper--with-transparent-layer':
          fields.showTransparentLayer?.value,
      }"
    >
      <base-image
        :mobile-url="fields.imageMobile?.value?.src"
        :mobile-alt="fields.imageMobile?.value?.alt"
        :tablet-url="fields.imageTablet?.value?.src"
        :tablet-alt="fields.imageTablet?.value?.alt"
        :desktop-url="fields.imageDesktop?.value?.src"
        :desktop-alt="fields.imageDesktop?.value?.alt"
        :mobile-dimensions="[
          Number(fields.imageMobile?.value?.width),
          Number(fields.imageMobile?.value?.height),
        ]"
        :tablet-dimensions="[
          Number(fields.imageTablet?.value?.width),
          Number(fields.imageTablet?.value?.height),
        ]"
        :desktop-dimensions="[
          Number(fields.imageDesktop?.value?.width),
          Number(fields.imageDesktop?.value?.height),
        ]"
        :full-width="true"
        :fetchpriority="index && !isContentCard ? 'low' : 'high'"
        :lazy-loading="index && !isContentCard ? 'lazy' : null"
        preload-image-name="HeroCarouselItem"
      />
    </div>
    <div
      class="hero-carousel-item__content"
      :class="{
        ...detectComponentVariant(variantedComponents.HERO_CAROUSEL),
      }"
    >
      <div class="container">
        <sc-text tag="span" class="hero-carousel-item__subtitle font-s" :field="fields.subtitle" />
        <sc-text
          :tag="headingTag"
          class="hero-carousel-item__title"
          :class="headerTitleClass"
          :field="fields.title"
        />
        <sc-text tag="p" class="hero-carousel-item__description" :field="fields.description" />
        <div v-if="showCta || showSecondaryCta" class="hero-carousel-item__cta-wrapper">
          <base-button
            v-if="showCta"
            variant="primary"
            type="link"
            class="hero-carousel-item__cta hero-carousel-item__cta--primary"
            :link="fields.link?.value"
            :tabindex="swiperSlide?.isVisible || !isCarousel ? null : -1"
            @click="handleClickCTA(fields.link?.value?.text)"
          />
          <base-button
            v-if="showSecondaryCta"
            variant="secondary"
            type="link"
            class="hero-carousel-item__cta hero-carousel-item__cta--secondary"
            :link="fields.secondaryLink?.value"
            :tabindex="swiperSlide?.isVisible || !isCarousel ? null : -1"
            @click="handleClickCTA(fields.secondaryLink?.value?.text)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { Text as ScText } from '@sitecore-jss/sitecore-jss-vue';
import BaseButton from '../../2_elements/BaseButton/BaseButton.vue';
import BaseImage from '../../2_elements/BaseImage/BaseImage.vue';
import { useSwiperSlide } from 'swiper/vue';
import { useDetectVariantByBrand, variantedComponents } from '../../../utils/componentVariants';
import { useBrazeStore } from '../../../stores/brazeStore';
import { useButtonsAndLinksTracking } from '../../../data-layer/hooks/useButtonsAndLinksTracking';
import { useJssStore } from '../../../stores/jssStore';
import type { HeroCarouselItemProps } from '../types';

defineOptions({
  inheritAttrs: false,
});

const { trackButtonState } = useButtonsAndLinksTracking({
  componentName: 'hero_carousel',
});

const { logContentCardClick, logContentCardDisplay } = useBrazeStore();
const detectComponentVariant = useDetectVariantByBrand();

const jssStore = useJssStore();

const props = withDefaults(defineProps<HeroCarouselItemProps>(), {
  fields: () => ({}),
  isCarousel: false,
  headingType: 'h1',
  isContentCard: false,
});

const headingTag = computed(() => {
  if (props.headingType !== 'h1') return props.headingType;
  return props.index ? 'h2' : 'h1';
});

const headerTitleClass = computed(() => [
  ['GreeneKingInns', 'Belhaven', 'Metro'].includes(jssStore.brandName) ? 'font-xxl' : 'font-xl',
]);

const swiperSlide = props.isCarousel ? useSwiperSlide() : null;

const showCta = computed(
  () => !!props.fields.link?.value?.href && !!props.fields.link?.value?.text
);
const showSecondaryCta = computed(
  () => !!props.fields.secondaryLink?.value?.href && !!props.fields.secondaryLink?.value?.text
);

const handleClickCTA = (action: string) => {
  trackButtonState(action);
  if (props.isContentCard) logContentCardClick(props.contentCardId);
};

onMounted(() => {
  if (props.isContentCard) logContentCardDisplay(props.contentCardId);
});

defineExpose({ handleClickCTA });
</script>

<style lang="scss">
@import './scss/HeroCarouselItem.scss';
</style>
